/**
 * Phase 6 Plan 02 — HistorySection (D-132 composition root).
 *
 * Replaces the AssetSidePanel flat-list inspection region (Plan 04-04
 * lines 217-307) with the structured history timeline:
 *   <HistoryHeader>      — count + sort
 *   <FiltersAccordion>   — 10 filter dimensions (HIST-5.5)
 *   <ActiveFilterChips>  — visible only when at least one filter is active
 *   <ul of InspectionRow per filtered+sorted item>
 *
 * Multiple-row simultaneous expand via Set<inspection.id>; sort change
 * preserves the expanded set (HIST-5.4 invariant). Filter change unmounts
 * excluded rows; re-introduction reappears collapsed.
 *
 * @SpecRef("5.1")
 * @SpecRef("5.2")
 * @SpecRef("5.4")
 * @SpecRef("5.5")
 * @SpecRef("5.6")
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import type { InspectionRead } from "../../../api/inspections";
import { useAssetInspections } from "../useAssetInspections";
import { HistoryHeader } from "./HistoryHeader";
import { FiltersAccordion } from "./FiltersAccordion";
import { ActiveFilterChips } from "./ActiveFilterChips";
import { InspectionRow } from "./InspectionRow";
import {
  EMPTY_FILTERS,
  filterAndSort,
  type HistoryFilters,
  type SortKey,
} from "./filterLogic";

interface HistorySectionProps {
  assetId: number;
   
  setEditingInspection: (insp: InspectionRead) => void;
}

export function HistorySection({
  assetId,
  setEditingInspection,
}: HistorySectionProps) {
  const inspectionsQuery = useAssetInspections(assetId);
  const data: InspectionRead[] = inspectionsQuery.data ?? [];

  const [sortKey, setSortKey] = useState<SortKey>("date_desc");
  const [filters, setFilters] = useState<HistoryFilters>(EMPTY_FILTERS);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // 2026-05-06 — auto-expand the most recent inspection when the side panel
  // first opens for an asset. Operator UX: clicking a pin should immediately
  // surface the latest photos and tick state without an extra chevron click.
  // Re-arms when assetId changes so a fresh asset gets fresh seeding; held
  // by ref (not state) so it doesn't trample the user's explicit toggles
  // after the first auto-expand. SSE-driven additions to `data` after the
  // initial seed do NOT re-trigger expansion (lastSeedRef stays === assetId),
  // so an inspection saved while the operator is reviewing older history
  // does not yank their view.
  const lastSeedRef = useRef<number | null>(null);
  useEffect(() => {
    if (lastSeedRef.current !== assetId) {
      // Asset changed (or first mount) — clear the prior asset's expansion
      // state and re-arm auto-seed for the new asset.
      setExpandedIds(new Set());
      lastSeedRef.current = null;
    }
    if (lastSeedRef.current === null && data.length > 0) {
      const latest = [...data].sort((a, b) =>
        b.saved_at.localeCompare(a.saved_at),
      )[0];
      setExpandedIds(new Set([latest.id]));
      lastSeedRef.current = assetId;
    }
  }, [assetId, data]);

  const visible = useMemo(
    () => filterAndSort(data, filters, sortKey),
    [data, filters, sortKey],
  );

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onClearAll = () => setFilters(EMPTY_FILTERS);

  return (
    <section
      className="flex flex-col gap-sm"
      data-testid={`history-section-${assetId}`}
    >
      <HistoryHeader
        shown={visible.length}
        total={data.length}
        sortKey={sortKey}
        onSortChange={setSortKey}
      />
      <FiltersAccordion
        data={data}
        filters={filters}
        onChange={setFilters}
        onClearAll={onClearAll}
      />
      <ActiveFilterChips
        filters={filters}
        onChange={setFilters}
        onClearAll={onClearAll}
      />
      {visible.length === 0 ? (
        <p className="text-base text-text-secondary" data-testid="history-empty">
          <FormattedMessage id="LABEL_NO_INSPECTIONS_YET" />
        </p>
      ) : (
        <ul
          className="flex flex-col gap-xs"
          data-testid="history-timeline"
        >
          {visible.map((insp) => (
            <InspectionRow
              key={insp.id}
              inspection={insp}
              expanded={expandedIds.has(insp.id)}
              onToggleExpand={() => toggleExpand(insp.id)}
              onEdit={setEditingInspection}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
